import React, { useEffect, useMemo, useRef, useState } from 'react';

type VisibilitySensorProps = {
  partialVisibility?: boolean;
};

type LazyImageProps = {
  src: string;
  placeholder?: string;
  visibilitySensorProps?: VisibilitySensorProps;
  children: (src: string) => React.ReactNode;
};

const FALLBACK_ROOT_MARGIN = '200px';

const LazyImage: React.FunctionComponent<LazyImageProps> = ({
  src,
  placeholder,
  visibilitySensorProps,
  children,
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const rootMargin = useMemo(() => {
    if (visibilitySensorProps?.partialVisibility) {
      return FALLBACK_ROOT_MARGIN;
    }

    return '0px';
  }, [visibilitySensorProps?.partialVisibility]);

  useEffect(() => {
    if (!src) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold: 0,
      }
    );

    const target = hostRef.current;

    if (target) {
      observer.observe(target);
    }

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, src]);

  const resolvedSrc = isVisible ? src : placeholder || src;

  return <div ref={hostRef}>{children(resolvedSrc)}</div>;
};

export default LazyImage;